import { type } from 'arktype';

export const signInCredentialsSchema = type({
  'email?': type('string').configure({ description: 'Login email address of the user.', examples: ['anna.mueller@example.ch'] }),
  'username?': type('string').configure({ description: 'Login username of the user.', examples: ['anna.mueller'] }),
  password: type('string').configure({
    description: 'Plaintext password. Transmitted over HTTPS and never stored in plaintext server-side.',
    examples: ['correct-horse-battery-staple'],
  }),
  'rememberMe?': type('boolean').configure({
    description: 'When true, issues a long-lived session cookie so the browser stays signed in across restarts. Optional.',
    examples: [true, false],
  }),
}).configure({ description: 'Credentials payload for the configured password sign-in endpoint.' });

export type SignInCredentials = typeof signInCredentialsSchema.t;

export const signUpCredentialsSchema = type({
  name: type('string').configure({
    description: 'Display name of the new user.',
    examples: ['Anna Müller'],
  }),
  'email?': type('string').configure({
    description: 'Email address of the new user. Must be unique across the tenant.',
    examples: ['anna.mueller@example.ch'],
  }),
  'username?': type('string').configure({
    description: 'Username of the new user. Must be unique across the tenant.',
    examples: ['anna.mueller'],
  }),
  password: type('string').configure({
    description: 'Plaintext password meeting the tenant password policy. Transmitted over HTTPS only.',
    examples: ['correct-horse-battery-staple'],
  }),
}).configure({ description: 'Credentials payload for the self-service sign-up endpoint.' });

export type SignUpCredentials = typeof signUpCredentialsSchema.t;

export interface AuthUser {
  id: string;
  email: string;
  username?: string | null;
  name: string | null;
  image?: string | null;
  emailVerified: boolean;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface AuthSession {
  session: {
    id: string;
    userId: string;
    expiresAt: string | Date;
    token: string;
  };
  user: AuthUser;
}

/**
 * Information about a social login provider available to the frontend.
 */
export interface SocialProviderInfo {
  /** Provider identifier (e.g., 'microsoft') */
  providerId: string;
  /** Human-readable display name (e.g., 'Microsoft') */
  displayName: string;
}

// Re-export PasswordPolicy for use in controllers that import from @zag/auth/models
export type { PasswordPolicy } from './password-validator';

/**
 * Auth mode information returned by `GET /api/auth-mode`.
 *
 * The frontend uses this to dynamically render the correct login UI
 * (email/password form, social login buttons, or both).
 */
export interface AuthModeInfo {
  /**
   * Normalized list of enabled authentication methods.
   *
   * Always an array — e.g. `['emailPassword']`, `['social']`,
   * or `['emailPassword', 'social']`.
   */
  authModes: ('emailPassword' | 'social')[];
  /** Whether the email/password form should be shown */
  emailPasswordEnabled: boolean;
  /** Identifier used by credential sign-in and sign-up. */
  credentialIdentifier: 'email' | 'username';
  /** Username constraints when credentialIdentifier is `username`. */
  usernamePolicy: { minLength: number; maxLength: number } | null;
  /** List of available social login providers */
  socialProviders: SocialProviderInfo[];
}
